/* eslint-disable max-lines, react/jsx-no-bind */
import { useEffect, useRef } from 'react';
import ReactSimpleKeyboard from 'react-simple-keyboard';

import './keyboard.css';

const keyboardLayout = {
  default: [
    'a z e r t y u i o p',
    'q s d f g h j k l m',
    '{shift} w x c v b n {backspace}',
    '{numbers} {space} {ent}',
  ],
  shift: [
    'A Z E R T Y U I O P',
    'Q S D F G H J K L M',
    '{shift} W X C V B N {backspace}',
    '{numbers} {space} {ent}',
  ],
  numbers: ['1 2 3', '4 5 6', '7 8 9', '{abc} 0 {backspace}'],
};

const keyboardDisplay = {
  '{numbers}': '123',
  '{ent}': 'return',
  '{backspace}': '⌫',
  '{shift}': '⇧',
  '{abc}': 'ABC',
  '{space}': ' ',
};

function Keyboard({
  value = '', onChange, isOpen = false, onClose, layoutName = 'default', onHeightChange,
}) {
  const keyboard = useRef();
  const keyboardContainer = useRef();

  useEffect(() => {
    if (keyboard.current && keyboard.current.getInput() !== value) {
      keyboard.current.setInput(value);
    }
  }, [value]);

  useEffect(() => {
    if (keyboard.current) {
      keyboard.current.setOptions({ layoutName });
    }
  }, [layoutName]);

  useEffect(() => {
    if (!isOpen) return undefined;

    function handleOutsideClick(event) {
      if (keyboardContainer.current && event.composedPath().includes(keyboardContainer.current)) {
        return;
      }

      const target = event.target instanceof Element ? event.target : null;
      const clickedKeyboard = target?.closest('.keyboard');
      const clickedKeyboardTarget = target?.closest('[data-keyboard-target]');

      if (!clickedKeyboard && !clickedKeyboardTarget) {
        onClose();
      }
    }

    document.addEventListener('click', handleOutsideClick);

    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !keyboardContainer.current || !onHeightChange) return undefined;

    const resizeObserver = new ResizeObserver(([entry]) => {
      onHeightChange(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
    });

    resizeObserver.observe(keyboardContainer.current);

    return () => resizeObserver.disconnect();
  }, [isOpen, onHeightChange]);

  function setKeyboardRef(instance) {
    keyboard.current = instance;
  }

  function handleKeyPress(button) {
    const currentLayout = keyboard.current.options.layoutName;

    if (button === '{shift}' || button === '{lock}') {
      keyboard.current.setOptions({
        layoutName: currentLayout === 'default' ? 'shift' : 'default',
      });
    }

    if (button === '{numbers}' || button === '{abc}') {
      keyboard.current.setOptions({
        layoutName: currentLayout !== 'numbers' ? 'numbers' : 'default',
      });
    }
  }

  function handlePointerDown(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  if (!isOpen) return null;

  return (
    <div
      ref={keyboardContainer}
      className="keyboard"
      onPointerDown={handlePointerDown}
    >
      <ReactSimpleKeyboard
        keyboardRef={setKeyboardRef}
        onChange={onChange}
        onKeyPress={handleKeyPress}
        layout={keyboardLayout}
        layoutName={layoutName}
        display={keyboardDisplay}
        buttonAttributes={[{ attribute: 'type', value: 'button' }]}
      />
    </div>
  );
}

export default Keyboard;